import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BookAppointmentService } from '../book-appointment.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { NgClass, CommonModule } from '@angular/common';
import { MatInput } from '@angular/material/input';
import { MatSelectModule } from "@angular/material/select";
import { MatCheckbox } from "@angular/material/checkbox";
import { AppService } from 'src/app/services/app.service';
import { ObjectType } from 'src/app/services/client.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-customer-info',
  templateUrl: './customer-info.component.html',
  styleUrls: ['./customer-info.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
  MatFormField,
  NgClass,
  CommonModule,
    MatLabel,
    MatInput,
    MatError,
    MatSelectModule,
    MatCheckbox,
    MatIconModule
  ]
})
export class CustomerInfoComponent implements OnInit, AfterViewInit {

  customerInfoForm!: FormGroup;
  preferredModeOfContact: string[] = ['Mail', 'Call', 'Text', 'Any'];
  noteLength = 0;
  customerName:string = $localize`Enter your name`;
  phoneNumber:string = $localize`Enter your contact number`;
  emailAddress:string = $localize`Enter your Email`;
  paymentMethod:string = $localize`Select payment method`;
  notePlaceholder:string = $localize`Add note here`;

  validationMessages = {
    name: { required: $localize`Name is required` },
    phoneNo: {
      required: $localize`Phone number is required`,
      maxlength: $localize`Phone number cannot exceed 15 characters.` ,
      pattern: $localize`Phone number can contain digits only`},
    email: {
      required: $localize`Email is required`,
      email: $localize`Enter a valid email`
    },
    consent: { required: $localize`This checkbox is required` }
  };

  paymentmethod: ObjectType[] = [];
  formSubmitted = false;
  @ViewChild('consentSection') consentSection!: ElementRef;
  @ViewChild('noteTextarea') noteTextareaRef!: ElementRef<HTMLTextAreaElement>;

  constructor(
    private fb: FormBuilder,
    private bookAppointmentService: BookAppointmentService,
    private sessionStorageService: SessionStorageService,
    private appService: AppService  ) { }

  ngOnInit(): void {
    this.initForm();
    this.subscribeFormChanges();
    this.getPaymentMethods();
    this.trackNoteLength();
  }

  ngAfterViewInit(): void {
    this.restoreFormFromSession();
    setTimeout(() => this.autoResize(true));
  }

  private initForm(): void {
    this.customerInfoForm = this.fb.group({
      name: [null, Validators.required],
      phoneNo: [null, [Validators.required, Validators.maxLength(15), Validators.pattern('^\\+?[0-9 ]*$')]],
      email: [null, [Validators.required, Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]{2,}\.[a-zA-Z]{2,}$/)]],
      consent: [false, Validators.requiredTrue],
      contactType: [null],
      note: [null, [Validators.maxLength(200)]]
    });
  }

  private subscribeFormChanges(): void {
    this.customerInfoForm.valueChanges.subscribe(() => {
      const requiredFieldsValid =
        this.customerInfoForm.get('name')?.valid &&
        this.customerInfoForm.get('phoneNo')?.valid &&
        this.customerInfoForm.get('email')?.valid &&
        this.customerInfoForm.get('consent')?.valid;

      this.bookAppointmentService.updateCustomerInfoFormStatus(!!requiredFieldsValid);

      const sessionDataString = this.sessionStorageService.getSessionItem('oabAppSessionData');
      const sessionData = sessionDataString ? JSON.parse(sessionDataString) : null;
      const customerInfo = this.customerInfoForm.value;

      const customerInfoObject: any = {
        dealership: sessionData?.dealership,
        vehicleInfo: sessionData?.vehicleInfo,
        appointment: sessionData?.appointment,
        customerInfo
      };

      this.sessionStorageService.setSessionItem(
        'oabAppSessionData',
        JSON.stringify(customerInfoObject)
      );
    });
  }

  private trackNoteLength(): void {
    const control = this.customerInfoForm.get('note');
    if (control) {
      control.valueChanges.subscribe(value => {
        this.noteLength = value ? value.length : 0;
      });
    }
  }

  autoResize(forceBaselineWhenEmpty: boolean = false): void {
    const textarea = this.noteTextareaRef?.nativeElement;
    if (!textarea) return;
    const baseline = 56; 
    const value = textarea.value?.trim() ?? '';
    if (!value && forceBaselineWhenEmpty) {
      textarea.style.height = baseline + 'px';
      return;
    }
    textarea.style.height = 'auto';
    const newHeight = Math.max(baseline, textarea.scrollHeight);
    textarea.style.height = newHeight + 'px';
  }

  private restoreFormFromSession(): void {
    const sessionDataString = this.sessionStorageService.getSessionItem('oabAppSessionData');
    const sessionData = sessionDataString ? JSON.parse(sessionDataString) : null;

    if (sessionData && sessionData.customerInfo) {
      setTimeout(() => {
        const restored = { ...sessionData.customerInfo };
        if (typeof restored.note === 'string' && restored.note.length > 200) {
          restored.note = restored.note.slice(0, 200);
        }
        this.customerInfoForm.patchValue(restored);
        this.autoResize(true);
      });
    }
  }

  getPaymentMethods(): void {
    this.appService.getPaymentMethods().subscribe({
      next: (response: any) => {
        this.paymentmethod = response as ObjectType[];
      },
      error: (err) => {
        this.paymentmethod = [];
        this.appService.handleErrorMessage(err);
      }
    });
  }

  onContactTypeSelect(contactType: string): void {
    this.customerInfoForm.controls['contactType'].patchValue(contactType);
  }

  onSubmit(): void {
    this.formSubmitted = true;
  }

  scrollToConsent() {
    if (this.consentSection) {
      this.consentSection.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }

  clearNote(): void {
  this.customerInfoForm.get('note')?.setValue('');
  const textArea = this.noteTextareaRef?.nativeElement;
  if (textArea) {
    textArea.style.height = '56px';
    textArea.focus();
  }
  }
}
